import React from 'react';
import { IndianRupee, FileText, CheckCircle } from 'lucide-react';
import { Order } from '../../types/order';
import { formatDateTime, formatDate } from '../../utils/date';
import { formatPrice } from '../../utils/utility';

interface PaymentOrderSummaryProps {
  order: Order;
  onCollectRemaining?: () => void;
  isCollecting?: boolean;
}

const PaymentOrderSummary: React.FC<PaymentOrderSummaryProps> = ({ order, onCollectRemaining, isCollecting }) => {
  const isPartialCOD = order.paymentOption === 'PARTIAL_COD';
  const isCOD = order.paymentOption === 'COD';
  const partialPaid = Number(order.partialPaymentAmount || 0);
  const hasPartialCOD = isCOD && partialPaid > 0;
  const codDueAmount = isCOD ? Math.max(order.amount - partialPaid, 0) : 0;
  const partialCodDue = isPartialCOD ? Math.max(order.amount - partialPaid, 0) : 0;
  const isFullyCollected = order.paymentStatus === 'PAID';
  const isPartiallyPaid = order.paymentStatus === 'PARTIALLY_PAID';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Payment Information */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <IndianRupee className="w-5 h-5 mr-2" />
            Payment Information
          </h2>
        </div>
        <div className="px-6 py-4">
          <div className="space-y-3">
            {/* Amount paid now (from payment object) */}
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Amount:</span>
              <span className="text-sm font-medium text-gray-900">{order.payment ? formatPrice(order.payment.amount) : 'N/A'}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Payment option:</span>
              <span className="text-sm text-gray-900">{order.payment?.paymentOption || order.paymentOption}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Payment method:</span>
              <span className="text-sm text-gray-900">{order.payment?.paymentMethod || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Date:</span>
              <span className="text-sm text-gray-900">{order.payment ? formatDateTime(order.payment.createdAt) : 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Status:</span>
              <span className={`text-sm font-medium ${order.payment?.status === 'COMPLETED' ? 'text-green-600' :
                order.payment?.status === 'PENDING' ? 'text-yellow-600' : 'text-red-600'
                }`}>
                {order.payment?.status || 'N/A'}
              </span>
            </div>

            {/* Legacy partial COD note (COD + partialPaymentAmount > 0) */}
            {hasPartialCOD && (
              <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-3">
                <p className="text-sm font-medium text-amber-900">COD collection pending</p>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between">
                    <span className="text-sm text-amber-800">Prepaid collected:</span>
                    <span className="text-sm font-medium text-amber-900">{formatPrice(partialPaid)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-amber-800">Collect on delivery:</span>
                    <span className="text-sm font-medium text-amber-900">{formatPrice(codDueAmount)}</span>
                  </div>
                </div>
                <p className="mt-2 text-xs text-amber-800">
                  Total order amount is {formatPrice(order.amount)}. Customer already paid {formatPrice(partialPaid)}.
                </p>
              </div>
            )}

            {/* PARTIAL_COD breakdown */}
            {isPartialCOD && (
              <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-3">
                <p className="text-sm font-medium text-blue-900">Partial COD Order</p>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between">
                    <span className="text-sm text-blue-800">Advance paid online:</span>
                    <span className="text-sm font-medium text-blue-900">
                      {formatPrice(partialPaid)}
                      {order.advancePaymentOption && (
                        <span className="ml-1 text-xs text-blue-700">via {order.advancePaymentOption}</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-blue-800">Remaining at door:</span>
                    <span className="text-sm font-medium text-blue-900">{formatPrice(partialCodDue)}</span>
                  </div>
                </div>

                <div className="mt-3">
                  {isFullyCollected ? (
                    <div className="flex items-center gap-1.5 text-sm font-medium text-green-700">
                      <CheckCircle className="w-4 h-4" />
                      Fully Collected
                    </div>
                  ) : isPartiallyPaid ? (
                    <button
                      onClick={onCollectRemaining}
                      disabled={isCollecting}
                      className="w-full py-1.5 px-3 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isCollecting ? 'Marking...' : 'Mark Remaining Collected'}
                    </button>
                  ) : null}
                </div>
              </div>
            )}

            {/* Manual UPI Payment Details */}
            {order.payment?.paymentMethod === 'MANUAL_UPI' && order.payment?.paymentInfo && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Payment Verification Details</h3>

                {/* Transaction Amount */}
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Transaction Amount:</span>
                  <span className="text-sm font-medium text-gray-900">₹{order.payment?.paymentInfo?.txnAmount}</span>
                </div>

                {/* Transaction Date */}
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Transaction Date:</span>
                  <span className="text-sm text-gray-900">{order.payment?.paymentInfo?.txnDate ? formatDate(order.payment.paymentInfo.txnDate) : 'N/A'}</span>
                </div>

                {/* Customer Remarks */}
                {order.payment?.paymentInfo?.remarks && (
                  <div className="mb-3">
                    <span className="text-sm text-gray-600">Customer Remarks:</span>
                    <p className="text-sm text-gray-900 mt-1 p-2 bg-gray-50 rounded-md">{order.payment?.paymentInfo?.remarks}</p>
                  </div>
                )}

                {/* Receipt Image */}
                {order.payment?.paymentInfo?.receipt && (
                  <div className="mb-3">
                    <span className="text-sm text-gray-600 block mb-2">Payment Receipt:</span>
                    <div className="relative">
                      <img
                        src={order.payment?.paymentInfo?.receipt}
                        alt="Payment Receipt"
                        className="w-full max-w-sm h-auto rounded-md border border-gray-300 cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => window.open(order.payment?.paymentInfo?.receipt, '_blank')}
                      />
                      <button
                        onClick={() => window.open(order.payment?.paymentInfo?.receipt, '_blank')}
                        className="absolute top-2 right-2 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-1 shadow-sm transition-all"
                        title="View full size"
                      >
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Click to view full size</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Order Summary */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Order Summary
          </h2>
        </div>
        <div className="px-6 py-4">
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Subtotal:</span>
              <span className="text-sm text-gray-900">
                {formatPrice(order.items.reduce((total, item) => total + (item.mrp * item.quantity), 0))}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Discount:</span>
              <span className="text-sm text-green-600">
                -{formatPrice(order.items.reduce((total, item) => total + (item.discount * item.quantity), 0))}
              </span>
            </div>
            {order.coupon && (order.discount ?? 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Coupon ({order.coupon.code}):</span>
                <span className="text-sm text-green-600">-{formatPrice(order.discount!)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Delivery Charge:</span>
              <span className="text-sm text-gray-900">{formatPrice(order.deliveryCharge)}</span>
            </div>
            {(order.charges ?? []).map((c, i) => (
              <div key={i} className="flex justify-between">
                <span className="text-sm text-gray-600">{c.label}:</span>
                <span className="text-sm text-gray-900">{formatPrice(c.amount)}</span>
              </div>
            ))}
            <div className="border-t pt-3">
              <div className="flex justify-between">
                <span className="text-base font-medium text-gray-900">Total:</span>
                <span className="text-base font-medium text-gray-900">{formatPrice(order.amount)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentOrderSummary;
